/** Wraps chips in reading order, starting a new row whenever the next chip doesn't fit. Returns each row's chip indexes. */
function wrapChips(chipWidths: number[], containerWidth: number, gap: number): number[][] {
  const rows: number[][] = []
  let rowWidth = 0
  chipWidths.forEach((width, index) => {
    const currentRow = rows.at(-1)
    if (currentRow && rowWidth + gap + width <= containerWidth) {
      currentRow.push(index)
      rowWidth += gap + width
    } else {
      rows.push([index])
      rowWidth = width
    }
  })
  return rows
}

/** Width of a row holding the chips from `start` up to (not including) `end`. */
function getRowWidth(chipWidths: number[], start: number, end: number, gap: number): number {
  const chipCount = end - start
  if (chipCount <= 0) return 0
  return chipWidths.slice(start, end).reduce((sum, width) => sum + width, 0) + gap * (chipCount - 1)
}

/**
 * Lays chips out in up to two rows, in reading order. While they fit, they wrap. When they would need a third row,
 * they split into two rows at the point that keeps the longer row shortest, so the rows scroll sideways as little as
 * possible. Ties keep the top row the longer one. Returns each row's chip indexes, in order.
 */
export function splitChipsIntoRows({ chipWidths, containerWidth, gap }: { chipWidths: number[]; containerWidth: number; gap: number }): number[][] {
  const wrappedRows = wrapChips(chipWidths, containerWidth, gap)
  if (wrappedRows.length <= 2) return wrappedRows

  let bestSplitIndex = 1
  let bestLongerRowWidth = Infinity
  for (let splitIndex = 1; splitIndex < chipWidths.length; splitIndex++) {
    const longerRowWidth = Math.max(getRowWidth(chipWidths, 0, splitIndex, gap), getRowWidth(chipWidths, splitIndex, chipWidths.length, gap))
    // `<=` so a tie moves the split later, keeping the top row the longer one
    if (longerRowWidth <= bestLongerRowWidth) {
      bestSplitIndex = splitIndex
      bestLongerRowWidth = longerRowWidth
    }
  }

  const chipIndexes = chipWidths.map((_, index) => index)
  return [chipIndexes.slice(0, bestSplitIndex), chipIndexes.slice(bestSplitIndex)]
}
